import { LineaVenta } from "../models/LineaVentaModel";
import { UserPayload } from "../models/SiteModel";
import { apiFetch } from "./fetchService";
import { parseDecimalValue } from "../utils/NumberParser";

const URLAPI = process.env.REACT_APP_BASE_URL;

const unwrapApiData = (response: any) => response?.data ?? response?.Data ?? response;

const normalizeObject = (value: any) => {
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }

  return value ?? {};
};

const normalizeDetalle = (detalle: any): MesaDetalleResponse[] => {
  if (typeof detalle === "string") {
    try {
      return JSON.parse(detalle);
    } catch {
      return [];
    }
  }

  return Array.isArray(detalle) ? detalle : [];
};

export interface MesaAbiertaResponse {
  abierta: boolean;
  comanda?: MesaComandaResponse;
  detalle: MesaDetalleResponse[];
}

export interface MesaComandaResponse {
  idMesa: number;
  idMozo: number;
  idComanda: number;
  idLocal: number;
  idEmpleado: number;
  pv: number;
  empleado: string;
  fecha: string;
  total: number;
  idCaja: number;
  nombre: string;
}

export interface MesaDetalleResponse {
  idComanda: number;
  idLocal: number;
  pv: number;
  idProducto: number;
  producto: string;
  cantidad: number;
  unitario: number;
  enMesa: boolean;
  seProduce: boolean;
  esPadre: boolean;
}

export interface MesaGuardarOptions {
  pv?: number;
  idCaja?: number;
  idEmpleado?: number;
  eliminarAnterior?: boolean;
  idComandasOriginales?: number[];
  nombre?: string;
}

const getUnitario = (linea: LineaVenta): number => {
  const producto = linea.producto as any;
  return parseDecimalValue(producto.CONTADO ?? producto.contado ?? producto.Contado ?? producto.precio ?? producto.Precio ?? 0);
};

const mapLineaMesaToPayload = (linea: LineaVenta) => ({
  idProducto: Number((linea.producto as any).IDPRODUCTO ?? (linea.producto as any).idProducto ?? (linea.producto as any).IdProducto ?? 0),
  producto: String((linea.producto as any).PRODUCTO ?? (linea.producto as any).producto ?? (linea.producto as any).Producto ?? ""),
  cantidad: linea.cantidad,
  unitario: getUnitario(linea),
  enMesa: true,
  nota: linea.notas || ""
});

const agruparLineasMesa = (lineas: LineaVenta[]): LineaVenta[] => {
  const grupos = new Map<string, LineaVenta>();

  for (const linea of lineas) {
    const producto = linea.producto as any;
    const idProducto = Number(producto.IDPRODUCTO ?? producto.idProducto ?? producto.IdProducto ?? 0);
    const unitario = getUnitario(linea);
    const nota = linea.notas || "";
    const idComanda = linea.idComanda ?? 0;
    const key = `${idComanda}|${idProducto}|${unitario}|${nota}`;

    const existente = grupos.get(key);
    if (existente) {
      existente.cantidad += linea.cantidad;
    } else {
      grupos.set(key, {
        ...linea,
        producto: {
          ...linea.producto,
          IDPRODUCTO: idProducto,
          PRODUCTO: String(producto.PRODUCTO ?? producto.producto ?? producto.Producto ?? "")
        },
        cantidad: linea.cantidad,
        notas: nota
      });
    }
  }

  return Array.from(grupos.values());
};

export async function fetchGetMesaAbierta(
  user: UserPayload,
  idLocal: number,
  pv: number,
  numeroMesa: string,
  signal?: AbortSignal
): Promise<MesaAbiertaResponse> {
  const response = await apiFetch(
    user.token,
    `${URLAPI}/Mesa/ObtenerAbierta/${idLocal}/${pv}/${encodeURIComponent(numeroMesa)}`,
    { signal }
  );
  const mesa = normalizeObject(unwrapApiData(response));

  return {
    abierta: Boolean(mesa?.abierta ?? mesa?.Abierta),
    comanda: mesa?.comanda ?? mesa?.Comanda,
    detalle: normalizeDetalle(mesa?.detalle ?? mesa?.Detalle)
  };
}

export async function fetchGetMesaAbiertaPorNombre(
  user: UserPayload,
  idLocal: number,
  pv: number,
  nombre: string,
  signal?: AbortSignal
): Promise<MesaAbiertaResponse> {
  const response = await apiFetch(
    user.token,
    `${URLAPI}/Mesa/ObtenerAbiertaPorNombre/${idLocal}/${pv}?nombre=${encodeURIComponent(nombre)}`,
    { signal }
  );
  const mesa = normalizeObject(unwrapApiData(response));
  return {
    abierta: Boolean(mesa?.abierta ?? mesa?.Abierta),
    comanda: mesa?.comanda ?? mesa?.Comanda,
    detalle: normalizeDetalle(mesa?.detalle ?? mesa?.Detalle)
  };
}

export async function fetchGuardarComandaMesa(
  user: UserPayload,
  idLocal: number,
  numeroMesa: string,
  lineas: LineaVenta[],
  options: MesaGuardarOptions = {}
) {
  const pv = options.pv ?? 1;
  const lineasAgrupadas = agruparLineasMesa(lineas);
  const lineasExistentes = lineasAgrupadas.filter((linea) => Boolean(linea.idComanda));
  const lineasNuevas = lineasAgrupadas.filter((linea) => !linea.idComanda);
  const results: any[] = [];

  const gruposExistentes = lineasExistentes.reduce((acc: Record<number, LineaVenta[]>, linea) => {
    const idComanda = linea.idComanda ?? 0;
    if (!acc[idComanda]) {
      acc[idComanda] = [];
    }
    acc[idComanda].push(linea);
    return acc;
  }, {});

  for (const idComandaText of Object.keys(gruposExistentes)) {
    const idComanda = Number(idComandaText);
    results.push(await apiFetch(user.token, `${URLAPI}/Mesa/ModificarDetalle`, {
      method: "POST",
      body: JSON.stringify({
        idComanda,
        idLocal,
        pv,
        eliminarAnterior: options.eliminarAnterior ?? true,
        detalle: gruposExistentes[idComanda].map(mapLineaMesaToPayload)
      })
    }));
  }

  const idsActuales = Object.keys(gruposExistentes).map(Number);
  const idsAnular = (options.idComandasOriginales ?? []).filter((idComanda) => !idsActuales.includes(idComanda));
  if (idsAnular.length > 0) {
    results.push(await apiFetch(user.token, `${URLAPI}/Mesa/CambiarEstado`, {
      method: "POST",
      body: JSON.stringify({
        idComandas: idsAnular,
        estado: 0
      })
    }));
  }

  if (lineasNuevas.length > 0) {
    results.push(await apiFetch(user.token, `${URLAPI}/Mesa/Abrir`, {
      method: "POST",
      body: JSON.stringify({
        idMesa: Number(numeroMesa),
        nombre: options.nombre?.trim() ?? "",
        idLocal,
        idEmpleado: options.idEmpleado ?? 0,
        pv,
        idCaja: options.idCaja ?? 0,
        total: lineasNuevas.reduce((total, linea) => total + getUnitario(linea) * linea.cantidad, 0),
        detalle: lineasNuevas.map(mapLineaMesaToPayload)
      })
    }));
  }

  return {
    success: results.every((result) => Boolean(result?.success ?? true)),
    results
  };
}

export function mapMesaDetalleToLineaVenta(linea: MesaDetalleResponse): LineaVenta {
  const source = linea as any;
  return {
    producto: {
      IDPRODUCTO: Number(source.idProducto ?? source.IdProducto ?? source.IDPRODUCTO ?? 0),
      CODIGO: String(source.codigo ?? source.Codigo ?? source.CODIGO ?? ""),
      PRODUCTO: String(source.producto ?? source.Producto ?? source.PRODUCTO ?? ""),
      CONTADO: parseDecimalValue(source.unitario ?? source.Unitario ?? source.UNITARIO ?? 0)
    },
    cantidad: parseDecimalValue(source.cantidad ?? source.Cantidad ?? source.CANTIDAD ?? 0),
    idComanda: Number(source.idComanda ?? source.IdComanda ?? source.IDCOMANDA ?? 0)
  };
}
